// The Difference view rebases the forecast on the baseline: the baseline is flat zero, and the one
// selected comparison scenario is plotted as its running gap against it.
import { CM_ROUNDING_STEP } from "consts";

import { ContributionMarginYearBreakdown } from "models";

import { cumulativeGapByYear } from "pages/app/scenario-analysis/utils/keyInsightCards";

export interface DifferenceDatum {
  year: string;
  yearNum: number;
  /** Comparison minus baseline, summed over years 1..yearNum, in the farm's currency per ha. */
  difference: number;
}

/** Running difference per year, from the same gap the Breaks even card reports, so the two agree on every crossing. */
export const buildDifferenceSeries = (
  comparisonRows: ContributionMarginYearBreakdown[],
  baselineRows: ContributionMarginYearBreakdown[],
  projectionYears: number
): DifferenceDatum[] =>
  cumulativeGapByYear(comparisonRows, baselineRows, projectionYears).map(({ year, gap }) => ({
    year: String(year),
    yearNum: year,
    difference: gap,
  }));

/** Ticks never go finer than `roundCmOutcome`'s step, so no tick can read as a figure the house rounding would not print. */
const MIN_AXIS_STEP = CM_ROUNDING_STEP;
const TARGET_TICK_GAPS = 4;
// No 2.5: at the smallest magnitude it falls between two rounding steps, so its label would print as a different figure.
const NICE_STEP_MULTIPLES = [1, 2, 5, 10];

const niceStep = (rough: number): number => {
  if (!Number.isFinite(rough) || rough <= 0) return MIN_AXIS_STEP;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const multiple = NICE_STEP_MULTIPLES.find((candidate) => rough <= candidate * magnitude) ?? 10;
  return Math.max(MIN_AXIS_STEP, multiple * magnitude);
};

export interface DifferenceAxis {
  domain: [number, number];
  ticks: number[];
  /** Gradient stop putting the loss/gain boundary on the zero line: zero's fraction down the shaded shape's own box, which spans the data extremes rather than the domain. */
  zeroOffset: number;
}

/** Axis that always contains zero, with one whole step of air on each side so the endpoint label and the zero label have room. */
export const buildDifferenceAxis = (values: number[]): DifferenceAxis => {
  const dataMax = Math.max(0, ...values);
  const dataMin = Math.min(0, ...values);

  const step = niceStep((dataMax - dataMin) / TARGET_TICK_GAPS);
  const domainMax = Math.max(step, Math.ceil(dataMax / step) * step);
  const domainMin = Math.min(-step, Math.floor(dataMin / step) * step);

  const ticks: number[] = [];
  for (let tick = domainMin; tick <= domainMax + step / 2; tick += step) {
    // Re-derive from the step index rather than accumulating, so 2.5-multiples do not drift off zero.
    ticks.push(Math.round(tick / step) * step || 0);
  }

  const span = dataMax - dataMin;

  return {
    domain: [domainMin, domainMax],
    ticks,
    // An all-zero series has no area to shade; 1 keeps the degenerate shape on the gain colour.
    zeroOffset: span === 0 ? 1 : dataMax / span,
  };
};

/**
 * Whether the house rounding (`CM_ROUNDING_STEP`) would collapse a real gap to "€0". True
 * only just either side of the crossing — the one place this view exists to locate, and the one place
 * "€0" would hide it.
 */
export const roundsAwayToZero = (value: number): boolean => value !== 0 && Math.abs(value) < CM_ROUNDING_STEP;

/** The total printed at the endpoint: the difference at the end of the horizon. */
export const finalDifference = (series: DifferenceDatum[]): number => series[series.length - 1]?.difference ?? 0;
